import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fmtMoney, formatApiError, DOC_TYPE_META, fetchTemplates } from "@/lib/api";
import { PageHeader, Content } from "@/components/PageChrome";
import ClientPicker from "@/components/ClientPicker";
import { Plus, Trash2, Download, Save, ArrowLeft, RefreshCw, Layers, Building2 } from "lucide-react";
import { toast } from "sonner";

const todayIso = () => new Date().toISOString().slice(0, 10);
const newItem = () => ({ description: "", hsn_sac: "", quantity: 1, unit: "", unit_price: 0, tax_percent: 18, discount_percent: 0 });

function computeTotals(items) {
  let subtotal = 0, tax_total = 0, discount_total = 0;
  for (const it of items) {
    const q = Number(it.quantity || 0), p = Number(it.unit_price || 0);
    const d = Number(it.discount_percent || 0), t = Number(it.tax_percent || 0);
    const line = q * p;
    const disc = line * d / 100;
    const afterDisc = line - disc;
    const tax = afterDisc * t / 100;
    subtotal += afterDisc; tax_total += tax; discount_total += disc;
  }
  return { subtotal: +subtotal.toFixed(2), tax_total: +tax_total.toFixed(2), discount_total: +discount_total.toFixed(2), total: +(subtotal + tax_total).toFixed(2) };
}

function useDebounced(fn, delay = 700) {
  const timer = useRef();
  return useCallback((...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
}

export default function DocumentBuilder() {
  const { id, type: newType } = useParams(); // /documents/:id  OR  /documents/new/:type
  const isNew = !id;
  const nav = useNavigate();

  const [doc, setDoc] = useState(() => ({
    doc_type: newType || "sales",
    template: "swiss",
    number: "",
    issue_date: todayIso(),
    due_date: "",
    currency: "INR",
    client_id: "",
    client_snapshot: {},
    items: [newItem()],
    notes: "",
    terms: "",
    status: "draft",
    po_reference: "",
    project_name: "",
    milestone: "",
    period_from: "",
    period_to: "",
    payment_method: "",
    payment_reference: "",
    amount_paid: 0,
    related_invoice: "",
    tax_mode: "auto",
    place_of_supply: "",
    recurring_enabled: false,
    recurring_frequency: "",
    recurring_next_date: "",
    recurring_end_date: "",
  }));

  const [clients, setClients] = useState([]);
  // `activeProfile` is the company a NEW document is issued under, chosen by the
  // switcher. `issuer` is the company that owns the document being edited, which
  // can be a different one. They are separate because conflating them made the
  // preview print the active company's letterhead on another company's invoice.
  const [activeProfile, setActiveProfile] = useState(null);
  const [issuer, setIssuer] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      fetchTemplates().then(setTemplates).catch(() => {});
      api.get("/clients").then((r) => {
        if (!cancelled) setClients(r.data);
      }).catch(() => {});

      const active = await api.get("/company").then((r) => r.data).catch(() => null);
      if (cancelled) return;
      setActiveProfile(active);

      if (isNew) {
        setIssuer(active);
        if (active?.base_currency) {
          setDoc((d) => ({ ...d, currency: active.base_currency }));
        }
        return;
      }

      // An existing document belongs to the company that issued it, which is
      // not necessarily the active one. Loading its own profile is what keeps
      // the letterhead, bank details and GSTIN correct in the preview.
      const existing = await api.get(`/documents/${id}`)
        .then((r) => r.data)
        .catch((e) => {
          toast.error(formatApiError(e));
          return null;
        });
      if (cancelled || !existing) return;
      setDoc(existing);
      const owner = existing.company_id
        ? await api.get(`/companies/${existing.company_id}`).then((r) => r.data).catch(() => null)
        : null;
      if (cancelled) return;
      setIssuer(owner || active);
    };
    boot();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [id, isNew]);

  const totals = useMemo(() => computeTotals(doc.items || []), [doc.items]);

  // Send the issuing company explicitly. The backend falls back to the active
  // company when this is absent, which is right for a new document but wrong
  // when previewing another company's invoice while a different profile is
  // active.
  const withIssuer = useCallback(
    (payload) => ({
      ...payload,
      company_id: payload.company_id ?? doc.company_id ?? issuer?.id ?? "",
    }),
    [doc.company_id, issuer],
  );

  // ------ Preview generation (debounced) ------
  const generatePreview = useCallback(async (payload) => {
    setPreviewLoading(true);
    try {
      const res = await api.post("/documents/preview-pdf", payload, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
    } catch (e) {
      // ignore preview errors silently
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const debouncedPreview = useDebounced(generatePreview, 600);

  useEffect(() => {
    // Kick a preview whenever meaningful fields change
    debouncedPreview(withIssuer(doc));
    // eslint-disable-next-line
  }, [doc, withIssuer, debouncedPreview]);

  const setField = (k) => (e) => setDoc({ ...doc, [k]: e?.target ? e.target.value : e });
  const setItem = (i, patch) => setDoc({ ...doc, items: doc.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) });
  const removeItem = (i) => setDoc({ ...doc, items: doc.items.filter((_, idx) => idx !== i) });
  const addItem = () => setDoc({ ...doc, items: [...(doc.items || []), newItem()] });

  const pickClient = (client_id) => {
    if (!client_id) { setDoc({ ...doc, client_id: "", client_snapshot: {} }); return; }
    const c = clients.find((c) => c.id === client_id);
    setDoc({
      ...doc, client_id,
      client_snapshot: c ? {
        name: c.name, email: c.email, phone: c.phone, address: c.address,
        city: c.city, state: c.state, country: c.country, postal_code: c.postal_code, gstin: c.gstin,
      } : {},
    });
  };

  /** "Create <name>" inside the client picker hands off to client management. */
  const createClientFromPicker = (name) => {
    nav(`/app/clients?new=${encodeURIComponent(name || "")}`);
  };

  const save = async () => {
    setSaving(true);
    try {
      let saved;
      if (isNew) {
        const { data } = await api.post("/documents", withIssuer(doc)); saved = data;
        toast.success("Document created");
        nav(`/app/documents/${data.id}`, { replace: true });
      } else {
        const { data } = await api.put(`/documents/${id}`, withIssuer(doc)); saved = data;
        toast.success("Document saved");
      }
      setDoc(saved);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    if (isNew) { toast.info("Save the document first to download PDF."); return; }
    try {
      const res = await api.get(`/documents/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${doc.number || "document"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const changeStatus = async (status) => {
    if (isNew) { setDoc({ ...doc, status }); return; }
    try {
      const { data } = await api.patch(`/documents/${id}/status`, { status });
      setDoc(data); toast.success(`Marked ${status}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const generateNext = async () => {
    if (isNew) { toast.info("Save the recurring template first."); return; }
    try {
      const { data } = await api.post(`/documents/${id}/generate-next`);
      toast.success(`Generated ${data.number}`);
      // Refresh source doc to see updated next_date
      const { data: fresh } = await api.get(`/documents/${id}`);
      setDoc(fresh);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sectionByType = {
    invoice: "invoices", receipt: "receipts", note: "notes", po: "purchase-orders",
  };
  const backLink = `/app/${sectionByType[DOC_TYPE_META[doc.doc_type]?.section] || "invoices"}`;

  return (
    <>
      <PageHeader
        overline={DOC_TYPE_META[doc.doc_type]?.short || doc.doc_type}
        title={isNew ? `New ${DOC_TYPE_META[doc.doc_type]?.label}` : doc.number || "Document"}
        description={
          isNew
            ? `Issued by ${activeProfile?.name || "your active company"} · live PDF preview`
            : `Editing ${doc.number || "document"} · issued by ${issuer?.name || doc.company_name || "unknown company"}`
        }
        testid="page-builder"
        actions={
          <>
            <Link to={backLink} className="text-sm px-3 h-10 border border-[var(--border)] rounded-md inline-flex items-center gap-2 hover:bg-white" data-testid="btn-back">
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
            <button onClick={downloadPdf} disabled={isNew} className="text-sm px-3 h-10 border border-[var(--border)] rounded-md inline-flex items-center gap-2 hover:bg-white disabled:opacity-40" data-testid="btn-download-pdf">
              <Download className="w-4 h-4" /> PDF
            </button>
            <button onClick={save} disabled={saving} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="btn-save">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
            </button>
          </>
        }
      />
      <Content>
        {/*
          Editing a document from a company other than the active one. The
          details on this page come from the issuing company and cannot be
          switched, so say so instead of letting the user think the switcher
          controls this document.
        */}
        {!isNew && issuer && activeProfile && issuer.id !== activeProfile.id && (
          <div
            className="card-flat p-4 mb-5 border-l-4 border-l-blue-500 flex items-center justify-between gap-4 flex-wrap"
            data-testid="issuer-notice"
          >
            <div className="flex items-start gap-2.5">
              <Building2 className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <div className="text-sm">
                <span className="font-medium">Issued by {issuer.name || "another company"}</span>
                <span className="text-gray-600">
                  {" "}· letterhead, bank details and numbering come from this profile, not from
                  your active company ({activeProfile.name || "unnamed"}). This document stays with
                  {issuer.name || "its issuer"} if you switch.
                </span>
              </div>
            </div>
            <Link to="/app/companies" className="text-xs px-3 h-8 leading-8 border border-[var(--border)] rounded-md bg-white hover:bg-gray-50 shrink-0">
              Manage companies
            </Link>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* LEFT: Form */}
          <div className="space-y-5">
            {/* Doc type / template */}
            <section className="card-flat p-5">
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <div className="text-[11px] text-gray-600 mb-1.5 uppercase tracking-wider">Document Type</div>
                  <select value={doc.doc_type} onChange={setField("doc_type")} className="w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm" data-testid="doc-type">
                    {Object.entries(DOC_TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </label>
                <label className="block">
                  <div className="text-[11px] text-gray-600 mb-1.5 uppercase tracking-wider">Status</div>
                  <select value={doc.status} onChange={(e) => changeStatus(e.target.value)} className="w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm" data-testid="doc-status">
                    {["draft","sent","paid","unpaid","overdue","cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
              </div>
            </section>

            {/* Template picker */}
            <section className="card-flat p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="overline">Template</div>
                  <div className="text-sm font-medium mt-1">Pick a PDF design ({templates.length} available)</div>
                </div>
                <button type="button" onClick={() => setShowAllTemplates(true)} className="text-xs text-[var(--primary)] hover:underline inline-flex items-center gap-1" data-testid="btn-see-all-templates">
                  <Layers className="w-3 h-3" /> See all
                </button>
              </div>
              {/* Show first 8 templates as tiles */}
              <div className="grid grid-cols-4 gap-2">
                {(templates || []).slice(0, 8).map((t) => (
                  <button
                    key={t.id} type="button"
                    onClick={() => setDoc({ ...doc, template: t.id, accent_color: "" })}
                    className={`text-left p-2 border rounded-md transition-all ${doc.template === t.id ? "border-[var(--primary)] ring-2 ring-[var(--primary)]/20 bg-[var(--primary)]/5" : "border-[var(--border)] hover:border-gray-400"}`}
                    data-testid={`tpl-${t.id}`}
                  >
                    <div className="w-full h-8 rounded-sm mb-1.5" style={{ background: t.accent }}></div>
                    <div className="text-[11px] font-medium truncate">{t.name}</div>
                  </button>
                ))}
              </div>

              {/* Accent color override */}
              <div className="mt-4 pt-4 border-t border-[var(--border)]">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[11px] text-gray-600 uppercase tracking-wider">Accent color override</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">Optional — customize the highlight color for this doc.</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={doc.accent_color || (templates.find(t => t.id === doc.template)?.accent || "#0055FF")}
                      onChange={(e) => setDoc({ ...doc, accent_color: e.target.value })}
                      className="w-10 h-10 border border-[var(--border)] rounded cursor-pointer"
                      data-testid="accent-color-input"
                    />
                    {doc.accent_color && (
                      <button onClick={() => setDoc({ ...doc, accent_color: "" })} className="text-xs text-gray-500 hover:text-[var(--fg)] underline" data-testid="reset-accent">Reset</button>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Meta */}
            <section className="card-flat p-5">
              <div className="overline mb-3">Details</div>
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <div className="text-[11px] text-gray-600 mb-1.5 uppercase tracking-wider">Number <span className="text-gray-400">(auto if blank)</span></div>
                  <input value={doc.number || ""} onChange={setField("number")} className="w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm font-mono" data-testid="doc-number" />
                </label>
                <label className="block">
                  <div className="text-[11px] text-gray-600 mb-1.5 uppercase tracking-wider">Currency</div>
                  <select value={doc.currency} onChange={setField("currency")} className="w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm" data-testid="doc-currency">
                    <option value="INR">INR (₹)</option><option value="USD">USD ($)</option>
                  </select>
                </label>
                <label className="block">
                  <div className="text-[11px] text-gray-600 mb-1.5 uppercase tracking-wider">Issue Date</div>
                  <input type="date" value={doc.issue_date || ""} onChange={setField("issue_date")} className="w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm" data-testid="doc-issue-date" />
                </label>
                <label className="block">
                  <div className="text-[11px] text-gray-600 mb-1.5 uppercase tracking-wider">Due Date</div>
                  <input type="date" value={doc.due_date || ""} onChange={setField("due_date")} className="w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm" data-testid="doc-due-date" />
                </label>
              </div>
            </section>

            {/* Client */}
            <section className="card-flat p-5">
              <div className="overline mb-3">Bill To</div>
              <label className="block mb-3">
                <div className="text-[11px] text-gray-600 mb-1.5 uppercase tracking-wider">Existing Client</div>
                {/* Clients are shared across every company, so this list is the
                    whole account and does not change with the active company. */}
                <ClientPicker
                  clients={clients}
                  value={doc.client_id || ""}
                  onChange={pickClient}
                  onCreateNew={createClientFromPicker}
                  placeholder="Search your clients…"
                  testid="doc-client"
                />
                <input type="hidden" value={doc.client_id || ""} readOnly data-testid="doc-client-value" />
                <div className="mt-1.5 text-[10px] text-gray-500">
                  {clients.length} client{clients.length === 1 ? "" : "s"} available across all your companies.
                </div>
              </label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { k: "name", label: "Name", span: 2 },
                  { k: "email", label: "Email" },
                  { k: "phone", label: "Phone" },
                  { k: "address", label: "Address", span: 2 },
                  { k: "city", label: "City" }, { k: "state", label: "State" },
                  { k: "gstin", label: "GSTIN", span: 2 },
                ].map((f) => (
                  <label key={f.k} className={`block col-span-${f.span || 1}`}>
                    <div className="text-[10px] text-gray-500 mb-1 uppercase tracking-wider">{f.label}</div>
                    <input
                      value={doc.client_snapshot?.[f.k] || ""}
                      onChange={(e) => setDoc({ ...doc, client_snapshot: { ...doc.client_snapshot, [f.k]: e.target.value } })}
                      className="w-full h-9 px-2.5 border border-[var(--border)] rounded-md bg-white text-sm"
                      data-testid={`client-snap-${f.k}`}
                    />
                  </label>
                ))}
              </div>
            </section>

            {/* Type-specific fields */}
            {(doc.doc_type === "timesheet" || doc.doc_type === "recurring") && (
              <section className="card-flat p-5">
                <div className="overline mb-3">Period</div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <div className="text-[11px] text-gray-600 mb-1.5">Period From</div>
                    <input type="date" value={doc.period_from} onChange={setField("period_from")} className="w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm" data-testid="period-from" />
                  </label>
                  <label className="block">
                    <div className="text-[11px] text-gray-600 mb-1.5">Period To</div>
                    <input type="date" value={doc.period_to} onChange={setField("period_to")} className="w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm" data-testid="period-to" />
                  </label>
                </div>
              </section>
            )}
            {(doc.doc_type === "progress" || doc.doc_type === "purchase_order") && (
              <section className="card-flat p-5">
                <div className="overline mb-3">Project</div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <div className="text-[11px] text-gray-600 mb-1.5">Project Name</div>
                    <input value={doc.project_name} onChange={setField("project_name")} className="w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm" data-testid="project-name" />
                  </label>
                  <label className="block">
                    <div className="text-[11px] text-gray-600 mb-1.5">Milestone / PO Ref</div>
                    <input value={doc.milestone} onChange={setField("milestone")} className="w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm" data-testid="milestone" />
                  </label>
                </div>
              </section>
            )}
            {(doc.doc_type === "payment_receipt" || doc.doc_type === "expense_receipt") && (
              <section className="card-flat p-5">
                <div className="overline mb-3">Payment</div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <div className="text-[11px] text-gray-600 mb-1.5">Payment Method</div>
                    <input value={doc.payment_method} onChange={setField("payment_method")} placeholder="UPI / Card / NEFT" className="w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm" data-testid="payment-method" />
                  </label>
                  <label className="block">
                    <div className="text-[11px] text-gray-600 mb-1.5">Reference / Txn ID</div>
                    <input value={doc.payment_reference} onChange={setField("payment_reference")} className="w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm" data-testid="payment-ref" />
                  </label>
                </div>
              </section>
            )}
            {(doc.doc_type === "credit_note" || doc.doc_type === "debit_note") && (
              <section className="card-flat p-5">
                <label className="block">
                  <div className="text-[11px] text-gray-600 mb-1.5">Related Invoice #</div>
                  <input value={doc.related_invoice} onChange={setField("related_invoice")} className="w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm font-mono" data-testid="related-invoice" />
                </label>
              </section>
            )}

            {/* GST split (India) */}
            {doc.currency === "INR" && (
              <section className="card-flat p-5">
                <div className="overline mb-3">GST Split</div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <div className="text-[11px] text-gray-600 mb-1.5 uppercase tracking-wider">Tax Mode</div>
                    <select value={doc.tax_mode} onChange={setField("tax_mode")}
                      className="w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm" data-testid="doc-tax-mode">
                      <option value="auto">Auto (same-state → CGST+SGST, else IGST)</option>
                      <option value="cgst_sgst">CGST + SGST (intra-state)</option>
                      <option value="igst">IGST (inter-state)</option>
                      <option value="single">Single Tax (no split)</option>
                    </select>
                  </label>
                  <label className="block">
                    <div className="text-[11px] text-gray-600 mb-1.5 uppercase tracking-wider">Place of Supply</div>
                    <input value={doc.place_of_supply} onChange={setField("place_of_supply")} placeholder="e.g. Maharashtra"
                      className="w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm" data-testid="doc-place-of-supply" />
                  </label>
                </div>
                {doc.tax_mode === "auto" && (
                  <div className="mt-2 text-[11px] text-gray-500">Company state ({issuer?.state || "—"}) vs client state ({doc.client_snapshot?.state || "—"}) determines the split.</div>
                )}
              </section>
            )}

            {/* Recurring */}
            {doc.doc_type === "recurring" && (
              <section className="card-flat p-5" data-testid="recurring-section">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="overline">Recurring Schedule</div>
                    <div className="text-sm text-gray-600 mt-1">Auto-generate the next invoice on the scheduled date.</div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!doc.recurring_enabled}
                      onChange={(e) => setDoc({ ...doc, recurring_enabled: e.target.checked })}
                      data-testid="recurring-enabled" />
                    <span className="text-sm font-medium">Enable</span>
                  </label>
                </div>
                {doc.recurring_enabled && (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <label className="block">
                        <div className="text-[11px] text-gray-600 mb-1.5 uppercase tracking-wider">Frequency</div>
                        <select value={doc.recurring_frequency || ""} onChange={setField("recurring_frequency")}
                          className="w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm" data-testid="recurring-frequency">
                          <option value="">Select…</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="yearly">Yearly</option>
                        </select>
                      </label>
                      <label className="block">
                        <div className="text-[11px] text-gray-600 mb-1.5 uppercase tracking-wider">Next Date</div>
                        <input type="date" value={doc.recurring_next_date || ""} onChange={setField("recurring_next_date")}
                          className="w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm" data-testid="recurring-next-date" />
                      </label>
                      <label className="block">
                        <div className="text-[11px] text-gray-600 mb-1.5 uppercase tracking-wider">End Date <span className="text-gray-400">(optional)</span></div>
                        <input type="date" value={doc.recurring_end_date || ""} onChange={setField("recurring_end_date")}
                          className="w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm" data-testid="recurring-end-date" />
                      </label>
                    </div>
                    {!isNew && (
                      <div className="mt-4 pt-4 border-t border-[var(--border)] flex items-center justify-between">
                        <div className="text-xs text-gray-600">Save first, then click below to generate one invoice now for the current period.</div>
                        <button onClick={generateNext} className="text-sm px-4 h-9 border border-[var(--primary)] text-[var(--primary)] rounded-md hover:bg-[var(--primary)]/5 inline-flex items-center gap-2" data-testid="btn-generate-next">
                          <RefreshCw className="w-3 h-3" /> Generate next now
                        </button>
                      </div>
                    )}
                  </>
                )}
              </section>
            )}

            {/* Items */}
            <section className="card-flat p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="overline">Line Items</div>
                <button onClick={addItem} className="text-xs px-3 h-8 border border-[var(--border)] rounded-md inline-flex items-center gap-1.5 hover:bg-white" data-testid="add-item">
                  <Plus className="w-3 h-3" /> Add Row
                </button>
              </div>
              <div className="overflow-x-auto -mx-5 px-5">
                <table className="w-full text-sm">
                  <thead className="text-[10px] text-gray-500 uppercase tracking-wider">
                    <tr className="border-b border-[var(--border)]">
                      <th className="text-left py-2 font-medium">Description</th>
                      <th className="text-right py-2 font-medium w-16">Qty</th>
                      <th className="text-right py-2 font-medium w-24">Rate</th>
                      <th className="text-right py-2 font-medium w-16">Tax%</th>
                      <th className="text-right py-2 font-medium w-24">Amount</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody data-testid="items-table">
                    {(doc.items || []).map((it, i) => {
                      const q = Number(it.quantity || 0), p = Number(it.unit_price || 0), d = Number(it.discount_percent || 0);
                      const amount = q * p * (1 - d / 100);
                      return (
                        <tr key={i} className="border-b border-[var(--border)]">
                          <td className="py-2 pr-2">
                            <input value={it.description} onChange={(e) => setItem(i, { description: e.target.value })}
                              placeholder="Item or service" className="w-full h-9 px-2 border border-[var(--border)] rounded bg-white text-sm" data-testid={`item-desc-${i}`} />
                          </td>
                          <td className="py-2 pr-2">
                            <input type="number" step="0.01" value={it.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })}
                              className="w-full h-9 px-2 border border-[var(--border)] rounded bg-white text-sm text-right font-mono" data-testid={`item-qty-${i}`} />
                          </td>
                          <td className="py-2 pr-2">
                            <input type="number" step="0.01" value={it.unit_price} onChange={(e) => setItem(i, { unit_price: e.target.value })}
                              className="w-full h-9 px-2 border border-[var(--border)] rounded bg-white text-sm text-right font-mono" data-testid={`item-price-${i}`} />
                          </td>
                          <td className="py-2 pr-2">
                            <input type="number" step="0.01" value={it.tax_percent} onChange={(e) => setItem(i, { tax_percent: e.target.value })}
                              className="w-full h-9 px-2 border border-[var(--border)] rounded bg-white text-sm text-right font-mono" data-testid={`item-tax-${i}`} />
                          </td>
                          <td className="py-2 text-right font-mono text-sm num" data-testid={`item-amount-${i}`}>{fmtMoney(amount, doc.currency)}</td>
                          <td className="py-2 text-right">
                            <button onClick={() => removeItem(i)} className="p-1.5 hover:bg-gray-100 rounded text-red-600" data-testid={`item-remove-${i}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="mt-4 flex justify-end">
                <div className="min-w-[280px] space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span className="num" data-testid="totals-subtotal">{fmtMoney(totals.subtotal, doc.currency)}</span></div>
                  {totals.discount_total > 0 && <div className="flex justify-between"><span className="text-gray-500">Discount</span><span className="num text-red-600">- {fmtMoney(totals.discount_total, doc.currency)}</span></div>}
                  <div className="flex justify-between"><span className="text-gray-500">Tax / GST</span><span className="num" data-testid="totals-tax">{fmtMoney(totals.tax_total, doc.currency)}</span></div>
                  <div className="flex justify-between border-t border-[var(--border)] pt-2 text-lg font-medium"><span>Total</span><span className="num" data-testid="totals-total">{fmtMoney(totals.total, doc.currency)}</span></div>
                </div>
              </div>
            </section>

            {/* Notes/Terms */}
            <section className="card-flat p-5 grid grid-cols-2 gap-4">
              <label className="block">
                <div className="text-[11px] text-gray-600 mb-1.5 uppercase tracking-wider">Notes</div>
                <textarea rows={3} value={doc.notes} onChange={setField("notes")} className="w-full p-2.5 border border-[var(--border)] rounded-md bg-white text-sm" data-testid="doc-notes" />
              </label>
              <label className="block">
                <div className="text-[11px] text-gray-600 mb-1.5 uppercase tracking-wider">Terms</div>
                <textarea rows={3} value={doc.terms} onChange={setField("terms")} className="w-full p-2.5 border border-[var(--border)] rounded-md bg-white text-sm" data-testid="doc-terms" />
              </label>
            </section>
          </div>

          {/* RIGHT: Preview */}
          <aside>
            <div className="sticky top-6 card-flat overflow-hidden" data-testid="pdf-preview">
              <div className="px-4 py-3 border-b border-[var(--border)] flex items-center justify-between">
                <div className="overline">Live PDF Preview</div>
                <button onClick={() => generatePreview(doc)} className="text-xs text-gray-500 hover:text-[var(--fg)] inline-flex items-center gap-1" data-testid="preview-refresh">
                  <RefreshCw className={`w-3 h-3 ${previewLoading ? "animate-spin" : ""}`} /> Refresh
                </button>
              </div>
              <div className="bg-[var(--muted)] h-[calc(100vh-140px)] min-h-[820px]">
                {previewUrl ? (
                  <iframe
                    src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                    title="PDF preview"
                    className="w-full h-full"
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center text-sm text-gray-500">Generating preview…</div>
                )}
              </div>
              <div className="px-4 py-3 border-t border-[var(--border)] text-[11px] text-gray-500 font-mono flex items-center justify-between">
                <span>Template: {templates.find((t) => t.id === doc.template)?.name || doc.template}</span>
                <span>{doc.currency}</span>
              </div>
            </div>
          </aside>
        </div>
      </Content>

      {/* Template Gallery Modal */}
      {showAllTemplates && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => setShowAllTemplates(false)}>
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-[var(--border)] flex items-center justify-between">
              <div>
                <div className="overline">Templates</div>
                <div className="text-lg font-medium mt-0.5">Pick from {templates.length} designs</div>
              </div>
              <button className="p-1.5 hover:bg-gray-100 rounded text-sm" onClick={() => setShowAllTemplates(false)}>Close</button>
            </div>
            <div className="p-5 overflow-y-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {templates.map((t) => (
                <button
                  key={t.id} type="button"
                  onClick={() => { setDoc({ ...doc, template: t.id, accent_color: "" }); setShowAllTemplates(false); }}
                  className={`text-left p-3 border rounded-lg transition-all ${doc.template === t.id ? "border-[var(--primary)] ring-2 ring-[var(--primary)]/20" : "border-[var(--border)] hover:border-gray-400"}`}
                  data-testid={`gallery-tpl-${t.id}`}
                >
                  <div className="h-24 rounded-sm mb-3 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${t.accent}22, ${t.accent}44)` }}>
                    <div className="absolute top-2 left-2 h-2 w-8 rounded-full" style={{ background: t.accent }}></div>
                    <div className="absolute bottom-2 left-2 right-2 h-1 bg-white/70 rounded"></div>
                    <div className="absolute bottom-4 left-2 h-1 w-2/3 bg-white/50 rounded"></div>
                    <div className="absolute bottom-6 left-2 h-1 w-1/2 bg-white/40 rounded"></div>
                  </div>
                  <div className="text-sm font-medium">{t.name}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5 font-mono">{t.base}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
